/**
 * Los textos de una sección tal como están ahora, para llevarlos a otro diseño.
 *
 * Es el camino inverso de `applySectionCopy`: aquel reparte una `SectionCopy`
 * entre los huecos (`aiSlot`) de un bloque; éste lee esos mismos huecos de una
 * sección ya hecha —con lo que el cliente escribió a mano— y arma la
 * `SectionCopy` equivalente, con las mismas reglas de numeración. Así «Cambiar
 * diseño» en el editor hace lo mismo que «Otra variante» en el asistente:
 * repartir los mismos textos en otro bloque, sin IA.
 *
 * Además de los textos devuelve lo que `applySectionCopy` no lleva: los enlaces
 * de los botones (a dónde van) y las fotos, en su orden, separadas las de
 * personas (retratos) de las demás.
 *
 * Una sección sin huecos marcados se lee con los que se deducen
 * (`inferTemplateSlots`), como hace la IA con un bloque así.
 */
import { AI_SLOT_DEFINITIONS } from './aiSlots';
import { inferTemplateSlots } from './templateSlotInference';
const AUTO_NUMBERED = new Set(AI_SLOT_DEFINITIONS.filter((slot) => slot.multiple).map((slot) => slot.id));
const ORDINAL = /^\s*\d{1,3}[.)]?\s*$/;
/** Pone `value` en `key` si hay valor y el campo sigue vacío (el primero manda). */
function put(target, key, value) {
    if (value !== undefined && value !== '' && target[key] === undefined)
        target[key] = value;
}
const slotOf = (node) => (typeof node.aiSlot === 'string' ? node.aiSlot.trim() : '');
function splitSlot(slot) {
    const match = /^(.*)\.(\d+)$/.exec(slot);
    return match ? [match[1], Number(match[2])] : [slot, 0];
}
const isList = (slot) => slot === 'repeat.list' || slot === 'slot-list';
/** El texto de un nodo, si es suyo (no lo trae de datos: CMS u ofertas). */
function ownText(node) {
    if (node.contentBinding)
        return undefined;
    if (typeof node.content !== 'string')
        return undefined;
    const value = node.content.trim();
    return value || undefined;
}
/** Qué campo de un elemento de la lista guarda cada hueco. */
const ITEM_FIELD = {
    'repeat.item.headline': 'title', 'repeat.item.title': 'title', 'slot-item-title': 'title', headline: 'title', label: 'title',
    'repeat.item.body': 'body', 'slot-item-body': 'body', body: 'body', subheadline: 'body', quote: 'body',
    caption: 'role', 'person-role': 'role',
    'repeat.item.author': 'author', 'slot-item-author': 'author', author: 'author', 'person-name': 'author',
    question: 'question', answer: 'answer',
    'price.value': 'price', 'repeat.item.price': 'price', 'price.period': 'period',
    'stat.value': 'value', 'stat.label': 'label',
};
function setOnce(item, field, value) {
    if (!value || field === 'features')
        return;
    if (item[field] === undefined)
        item[field] = value;
}
/** Un elemento de una lista: cada hueco de dentro, en su campo. */
function readItem(node, actions) {
    const item = {};
    const visit = (current) => {
        const [base] = splitSlot(slotOf(current));
        if (base) {
            if (base === 'repeat.item.icon' || base === 'icon') {
                if (typeof current.iconName === 'string' && !item.icon)
                    item.icon = current.iconName;
            }
            else if (base === 'price.feature') {
                const value = ownText(current);
                if (value)
                    item.features = [...(item.features ?? []), value];
            }
            else if (base === 'action.primary' || base === 'repeat.item.action') {
                put(actions, 'primary', ownText(current));
            }
            else if (base === 'action.secondary' || base === 'action.link') {
                put(actions, 'secondary', ownText(current));
            }
            else if (base === 'stat.value') {
                const value = ownText(current);
                if (value && !ORDINAL.test(value))
                    setOnce(item, 'value', value);
            }
            else {
                const field = ITEM_FIELD[base];
                if (field)
                    setOnce(item, field, ownText(current));
            }
        }
        current.children.forEach(visit);
    };
    visit(node);
    return item;
}
/** Las listas que viven en un componente (preguntas, reseñas, logos). */
function behaviorItems(node) {
    const behavior = node.behavior;
    if (!behavior)
        return null;
    if (behavior.type === 'faq' && Array.isArray(behavior.items)) {
        return behavior.items
            .map((row) => ({ question: typeof row.question === 'string' ? row.question : undefined, answer: typeof row.answer === 'string' ? row.answer : undefined }))
            .filter((row) => row.question || row.answer)
            .map((row) => ({ ...(row.question ? { question: row.question } : {}), ...(row.answer ? { answer: row.answer } : {}) }));
    }
    if (behavior.type === 'reviews' && Array.isArray(behavior.reviews)) {
        return behavior.reviews.flatMap((row) => {
            const item = {};
            if (typeof row.name === 'string' && row.name.trim())
                item.author = row.name.trim();
            if (typeof row.meta === 'string' && row.meta.trim())
                item.role = row.meta.trim();
            if (typeof row.text === 'string' && row.text.trim())
                item.body = row.text.trim();
            return Object.keys(item).length ? [item] : [];
        });
    }
    if (behavior.type === 'logos' && Array.isArray(behavior.items)) {
        return behavior.items.flatMap((row) => (typeof row.text === 'string' && row.text.trim() ? [{ title: row.text.trim() }] : []));
    }
    return null;
}
export function readSectionCopy(elements, category) {
    const nodes = inferTemplateSlots(elements, category).elements;
    const copy = {};
    const items = [];
    const actionTexts = {};
    const actions = {};
    const images = [];
    const avatars = [];
    const looseFeatures = [];
    const seen = new Map();
    let listRead = false;
    const itemAt = (index) => {
        while (items.length <= index)
            items.push({});
        return items[index];
    };
    const keepAction = (which, node) => {
        if (actions[which])
            return;
        actions[which] = { href: node.href, interaction: node.interaction };
    };
    const collectImages = (node) => {
        if (node.type === 'image' && typeof node.src === 'string' && node.src && !node.contentBinding) {
            const [base] = splitSlot(slotOf(node));
            if (base.startsWith('media.avatar'))
                avatars.push(node.src);
            else if (base !== 'media.logo')
                images.push(node.src);
        }
        node.children.forEach(collectImages);
    };
    const visit = (node) => {
        const fromBehavior = !listRead ? behaviorItems(node) : null;
        if (fromBehavior?.length) {
            listRead = true;
            items.push(...fromBehavior);
            return;
        }
        const slot = slotOf(node);
        if (slot && isList(slot) && !listRead) {
            listRead = true;
            const listActions = {};
            for (const child of node.children) {
                const item = readItem(child, listActions);
                if (Object.keys(item).length)
                    items.push(item);
            }
            put(actionTexts, 'primary', listActions.primary);
            put(actionTexts, 'secondary', listActions.secondary);
            return;
        }
        if (slot) {
            const [base, numberedAt] = splitSlot(slot);
            const n = numberedAt === 0 && AUTO_NUMBERED.has(base) ? (seen.set(base, (seen.get(base) ?? 0) + 1).get(base)) : numberedAt;
            const value = ownText(node);
            switch (base) {
                case 'eyebrow':
                case 'slot-eyebrow':
                    put(copy, 'eyebrow', value);
                    break;
                case 'slot-title':
                    put(copy, 'headline', value);
                    break;
                case 'slot-subtitle':
                    put(copy, 'subheadline', value);
                    break;
                case 'slot-body':
                    put(copy, 'body', value);
                    break;
                case 'slot-cta':
                case 'navigation.cta':
                case 'action.primary':
                    if (value && !actionTexts.primary) {
                        actionTexts.primary = value;
                        keepAction('primary', node);
                    }
                    break;
                case 'action.secondary':
                case 'action.link':
                    if (value && !actionTexts.secondary) {
                        actionTexts.secondary = value;
                        keepAction('secondary', node);
                    }
                    break;
                case 'headline':
                    if (n <= 1)
                        put(copy, 'headline', value);
                    else
                        setOnce(itemAt(n - 2), 'title', value);
                    break;
                case 'subheadline':
                    if (n <= 1)
                        put(copy, 'subheadline', value);
                    else
                        setOnce(itemAt(n - 2), 'body', value);
                    break;
                case 'body':
                    if (n <= 1)
                        put(copy, 'body', value);
                    else
                        setOnce(itemAt(n - 2), 'body', value);
                    break;
                case 'price.feature':
                    if (value)
                        looseFeatures.push(value);
                    break;
                case 'column':
                    if (value && n >= 1) {
                        copy.columns ??= [];
                        copy.columns[n - 1] = value;
                    }
                    break;
                case 'icon':
                    if (n >= 1 && typeof node.iconName === 'string')
                        put(itemAt(n - 1), 'icon', node.iconName);
                    break;
                default: {
                    const field = ITEM_FIELD[base];
                    if (field && n >= 1 && !(base === 'stat.value' && value && ORDINAL.test(value)))
                        setOnce(itemAt(n - 1), field, value);
                }
            }
        }
        node.children.forEach(visit);
    };
    nodes.forEach(visit);
    nodes.forEach(collectImages);
    if (actionTexts.primary)
        copy.primaryAction = actionTexts.primary;
    if (actionTexts.secondary)
        copy.secondaryAction = actionTexts.secondary;
    /* Los puntos sueltos (un plan sin lista) son del primer elemento, como los reparte `applySectionCopy`. */
    if (looseFeatures.length) {
        const first = itemAt(0);
        first.features = [...(first.features ?? []), ...looseFeatures];
    }
    const realItems = items.filter((item) => Object.keys(item).length);
    if (realItems.length)
        copy.items = realItems;
    if (copy.columns)
        copy.columns = copy.columns.filter(Boolean);
    for (const key of Object.keys(copy))
        if (copy[key] === undefined)
            delete copy[key];
    return { copy, actions, images, avatars };
}
//# sourceMappingURL=sectionCopyRead.js.map